import { isPropertyRateLoyaltyEligible } from 'lib/hotels/hotelLoyaltyEligibility'

export interface GroupedPropertyRate {
  /**
   * Unique key used for grouping rates together (boardCode::inclusionKey).
   * This is the key used to ensure uniqueness when grouping and should be used as React key.
   */
  groupingKey: string

  /**
   * All rates within the group, sorted by refundability then loyalty eligibility.
   */
  rates: Array<App.PropertyRate>

  /**
   * Default selected rate — the first rate in the sorted display order.
   */
  defaultRateId: string
}

const LOYALTY_GROUP_KEY_SUFFIXES = ['hotelLoyaltyEligible', 'loyaltyEligible', 'loyalty'] as const

/**
 * Matches www-le-customer: strip board code and cancellation policy from groupKey, keep the
 * inclusion hash. Also normalises loyalty-specific suffixes so standard and loyalty-eligible
 * Sabre variants collapse into the same group.
 */
export function extractPackageGroupingKeyFromGroupKey(groupKey: string): string {
  const parts = groupKey.split('-')
  if (parts.length < 2) {
    return groupKey
  }

  const boardCode = parts[0]
  const inclusionKey = normalizeInclusionKeyForGrouping(parts.slice(2).join('-'))

  return `${boardCode}::${inclusionKey}`
}

function normalizeInclusionKeyForGrouping(inclusionKey: string): string {
  for (const suffix of LOYALTY_GROUP_KEY_SUFFIXES) {
    const suffixToken = `-${suffix}`
    if (inclusionKey.endsWith(suffixToken)) {
      return inclusionKey.slice(0, -suffixToken.length)
    }
  }

  return inclusionKey
}

function scoreRefundability(
  headlineCancellationPolicyType: App.PropertyRate['headlineCancellationPolicyType'],
): number {
  switch (headlineCancellationPolicyType) {
    case 'fullyRefundable':
      return 2
    case 'partiallyRefundable':
      return 1
    case 'nonRefundable':
      return 0
  }
}

function scoreLoyaltyEligibility(isLoyaltyEligible: boolean): number {
  return isLoyaltyEligible ? 1 : 0
}

/**
 * Groups property rates by board code and inclusions — collapsing refundability and
 * loyalty-eligible variants into one group (same approach as www-le-customer).
 */
export function groupPropertyRates(
  rates: Array<App.PropertyRate>,
  isLoyaltyProgramFeatureFlagEnabled: boolean,
  sabreHotelsEnabled: boolean,
): Array<GroupedPropertyRate> {
  const grouped = new Map<string, GroupedPropertyRate>()

  rates.forEach(rate => {
    const groupingKey = extractPackageGroupingKeyFromGroupKey(rate.groupKey)

    const existing = grouped.get(groupingKey) || {
      groupingKey,
      rates: [] as Array<App.PropertyRate>,
      defaultRateId: rate.rateId,
    }

    existing.rates.push(rate)
    grouped.set(groupingKey, existing)
  })

  for (const group of grouped.values()) {
    group.rates.sort((a, b) => {
      const refundabilityDiff =
        scoreRefundability(a.headlineCancellationPolicyType) -
        scoreRefundability(b.headlineCancellationPolicyType)
      if (refundabilityDiff !== 0) {
        return refundabilityDiff
      }

      const aLoyalty = isPropertyRateLoyaltyEligible(a, isLoyaltyProgramFeatureFlagEnabled, sabreHotelsEnabled)
      const bLoyalty = isPropertyRateLoyaltyEligible(b, isLoyaltyProgramFeatureFlagEnabled, sabreHotelsEnabled)
      return scoreLoyaltyEligibility(aLoyalty) - scoreLoyaltyEligibility(bLoyalty)
    })

    group.defaultRateId = group.rates[0]?.rateId ?? group.defaultRateId
  }

  return Array.from(grouped.values())
}
